import { IPromise } from 'angular';

import * as Common from '../Common/modules';
import * as Models from '../Models';
import { IncentiveProgram } from '../Models/CostCalculation/IncentiveProgram';
import { ContractTransfer, ContractTransferRequest } from '../Models/Customer/ContractTransfer';
import { Customer } from '../Models/Customer/Customer';

export interface ICustomerApiClient {
  getCustomerActions: (igsCustomerNumber: string) => ng.IPromise<Models.Customer.CustomerAction.ICustomerAction[]>;
  getCustomerEvents: (igsCustomerNumber: string) => ng.IPromise<Models.Customer.CustomerEvent.ICustomerEvent[]>;
  getCustomerEventsMap: (igsCustomerNumber: string) => ng.IPromise<Models.Customer.CustomerEventMap.CustomerEventMap>;
  getCustomerEventsForDisplay: (
    igsCustomerNumber: string,
  ) => ng.IPromise<Models.Customer.CustomerEvent.ICustomerEvent[]>;
  getCustomerEvent: (
    igsCustomerNumber: string,
    eventCode: string,
  ) => ng.IPromise<Models.Customer.CustomerEvent.ICustomerEvent>;
  getOpportunityHasSignedContract: (partnerSystemNumber: string) => ng.IPromise<boolean>;
  setCustomerEvent: (
    igsCustomerNumber: string,
    eventCode: string,
    customerEvent: Models.Customer.CustomerEvent.ICustomerEvent,
  ) => ng.IPromise<void>;
  getCustomerPartner: (igsCustomerNumber: string) => ng.IPromise<Models.PartnerModel.IPartnerModel>;
  getAllCustomerPartners: (igsCustomerNumber: string) => ng.IPromise<Models.PartnerModel.IPartnerModel[]>;
  getCustomerPartnerSettings: (igsCustomerNumber: string) => ng.IPromise<Models.PartnerSettingsModel.IPartnerSettings>;
  searchSolarAccounts(
    searchSolarAccounts: Models.Customer.SearchSolarAccountsRequest.ISearchSolarAccountsRequest,
  ): ng.IPromise<any>;
  getCustomerDetails: (
    getCustomerDetails: Models.Customer.SolarCustomerDetailsRequest.ISolarCustomerDetailsRequest,
  ) => ng.IPromise<any>;
  getOpportunityNumberByIgsCustomerNumber: (igsCustomerNumber: string) => ng.IPromise<string>;
  submitNoteCreation: (createNoteRequest: Models.Customer.CustomerNoteModel.ICustomerNoteModel) => ng.IPromise<any>;
  getLatestNotes: (getLatestNotes: Models.Customer.SolarNoteRequest.ISolarNoteRequest) => ng.IPromise<any>;
  getNoteTypes: () => ng.IPromise<any>;
  getAdditionalCustomerDetails: (
    getAdditionalDetailsRequest: Models.Customer.SolarAdditionalCustomerDetailsRequest.ISolarAdditionalCustomerDetailsRequest,
  ) => ng.IPromise<any>;
  getAllOtherNotes: (getAllOtherNotes: Models.Customer.SolarNoteRequest.ISolarNoteRequest) => ng.IPromise<any>;
  getCustomerEditables: (igsCustomerNumber: string) => ng.IPromise<any>;
  submitEditedCustomer: (
    editedCustomerProfile: Models.Customer.EditCustomerProfile.IEditCustomerProfile,
  ) => ng.IPromise<any>;
  editAssignedSalesRepresentatives: (
    igsCustomerNumber: string,
    editedCustomerProfile: Models.Customer.EditCustomerProfile.IEditCustomerProfile,
  ) => ng.IPromise<any>;
  getCurentSolarAccountBalance: (
    solarAccountBalanceRequest: Models.Customer.SolarAccountBalanceRequest.SolarAccountBalanceRequest,
  ) => ng.IPromise<any>;
  getCustomerTerritory: (
    igsCustomerNumber: string,
    partnerNumber: string,
    leadNumber: string,
  ) => ng.IPromise<Models.CostCalculation.Territory.Territory>;
  associateRegionToCustomer: (
    request: Models.Customer.AssociateRegionToCustomerRequest.IAssociateRegionToCustomerRequest,
  ) => ng.IPromise<Models.Customer.AssociateRegionToCustomerResponse.IAssociateRegionToCustomerResponse>;
  getCustomerAccountStage: (igsCustomerNumber: string) => ng.IPromise<string>;
  getAccountPrePtoView: (igsCustomerNumber: string) => ng.IPromise<any>;
  getAccountPostPtoView: (igsCustomerNumber: string) => ng.IPromise<any>;
  getCustomerIdentifiers: (igsCustomerNumber: string) => ng.IPromise<any>;
  getAvailableAccountingInformation: () => ng.IPromise<any>;
  getEstimatedUtilityDetails: (
    identifier: string,
  ) => ng.IPromise<Models.Customer.EstimatedUtilityDetail.IEstimatedUtilityDetail>;
  submitEstimatedUtilityDetails: (
    identifier: string,
    estimatedUtilityDetail: Models.Customer.EstimatedUtilityDetail.IEstimatedUtilityDetail,
  ) => ng.IPromise<null>;
  getContractOrContractAmendmentInProgress: (igsCustomerNumber: string) => ng.IPromise<boolean>;
  getContractOrRateAmendmentInProgress: (igsCustomerNumber: string) => ng.IPromise<boolean>;
  transferCustomersContract: (contractTransferRequest: ContractTransferRequest) => ng.IPromise<ContractTransfer>;
  isEventGroupCompletionEventInProgress: (igsCustomerNumber: string) => ng.IPromise<boolean>;
  getCancelReasons: (includeInactive?: boolean) => ng.IPromise<Models.Customer.CancelReason.ICancelReason[]>;
  cancelCustomer: (request: Models.Customer.CancelCustomerRequest.ICancelCustomerRequest) => ng.IPromise<void>;
  sendMyAccountLink: (igsCustomerNumber: string) => ng.IPromise<Models.BaseApiResponse.BaseApiResponse>;
  getActiveIncentivePrograms: (igsCustomerNumber: string) => ng.IPromise<IncentiveProgram[]>;
  getCustomer: (igsCustomerNumber: string) => ng.IPromise<Customer>;
  submitCustomerEventBypass: (igsCustomerNumber: string, eventCode: string) => ng.IPromise<null>;
  getAccount: (igsCustomerNumber: string) => ng.IPromise<Models.Customer.Account.Account>;
  removeActiveWriteOffStatus: (igsCustomerNumber: string) => ng.IPromise<any>;
  validatePrimaryEmailAddress: (igsCustomerNumber: string) => ng.IPromise<boolean>;
  reactivateCustomer: (identifer: string) => ng.IPromise<any>;
  getCommunicationTypes: () => ng.IPromise<any>;
}

export class CustomerApiClient implements ICustomerApiClient {
  public prefix: string;

  public static $inject = ["igs.apiClientService"];

  constructor(private igsApiClientService: Common.ApiClientService.Igs.Common.IApiClientService) {
    this.prefix = "Customer";
  }

  public getCustomerActions = (
    igsCustomerNumber: string,
  ): ng.IPromise<Models.Customer.CustomerAction.ICustomerAction[]> => {
    var url = `${this.prefix}/${igsCustomerNumber}/Actions`;
    var config = { errorHandler: () => {} };
    return this.igsApiClientService.get(url, config);
  };

  public getCustomerEvents = (
    igsCustomerNumber: string,
  ): ng.IPromise<Models.Customer.CustomerEvent.ICustomerEvent[]> => {
    var url = `${this.prefix}/${igsCustomerNumber}/Events`;
    var config = { errorHandler: () => {} };
    return this.igsApiClientService.get(url, config);
  };

  public getCustomerEventsMap = (
    igsCustomerNumber: string,
  ): ng.IPromise<Models.Customer.CustomerEventMap.CustomerEventMap> => {
    var url = `${this.prefix}/${igsCustomerNumber}/EventsMap`;
    var config = { errorHandler: () => {} };
    return this.igsApiClientService.get(url, config);
  };

  public getCustomerEventsForDisplay = (
    igsCustomerNumber: string,
  ): ng.IPromise<Models.Customer.CustomerEvent.ICustomerEvent[]> => {
    var url = `${this.prefix}/${igsCustomerNumber}/Events/Displayed`;
    var config = { errorHandler: () => {} };
    return this.igsApiClientService.get(url, config);
  };

  public getCustomerEvent = (
    igsCustomerNumber: string,
    eventCode: string,
  ): ng.IPromise<Models.Customer.CustomerEvent.ICustomerEvent> => {
    var url = `${this.prefix}/${igsCustomerNumber}/Event/${eventCode}`;
    var config = { errorHandler: () => {} };
    return this.igsApiClientService.get(url, config);
  };

  public getOpportunityHasSignedContract = (partnerSystemNumber: string): ng.IPromise<boolean> => {
    var url = `Opportunity/${partnerSystemNumber}/HasSignedContract`;
    var config = { errorHandler: () => {} };
    return this.igsApiClientService.get(url, config);
  };

  public setCustomerEvent = (
    igsCustomerNumber: string,
    eventCode: string,
    customerEvent: Models.Customer.CustomerEvent.ICustomerEvent,
  ) => {
    var url = `${this.prefix}/${igsCustomerNumber}/Event/${eventCode}`;
    return this.igsApiClientService.post<void>(url, customerEvent);
  };

  public getCustomerPartner = (igsCustomerNumber: string): angular.IPromise<Models.PartnerModel.IPartnerModel> => {
    var url = `${this.prefix}/${igsCustomerNumber}/Partner`;
    var config = { errorHandler: () => {} };
    return this.igsApiClientService.get(url, config);
  };

  public getAllCustomerPartners = (
    igsCustomerNumber: string,
  ): angular.IPromise<Models.PartnerModel.IPartnerModel[]> => {
    var url = `${this.prefix}/${igsCustomerNumber}/Partners`;
    var config = { errorHandler: () => {} };
    return this.igsApiClientService.get(url, config);
  };

  public getCustomerPartnerSettings = (
    igsCustomerNumber: string,
  ): angular.IPromise<Models.PartnerSettingsModel.IPartnerSettings> => {
    var url = `${this.prefix}/${igsCustomerNumber}/PartnerSettings`;
    var config = { errorHandler: () => {} };
    return this.igsApiClientService.get(url, config);
  };

  getAdditionalCustomerDetails(
    getAdditionalDetailsRequest: Models.Customer.SolarAdditionalCustomerDetailsRequest.ISolarAdditionalCustomerDetailsRequest,
  ): angular.IPromise<any> {
    var url = `${this.prefix}/GetAdditionalCustomerDetails`;
    return this.igsApiClientService.post(url, getAdditionalDetailsRequest);
  }


  public searchSolarAccounts = (
    searchSolarAccounts: Models.Customer.SearchSolarAccountsRequest.ISearchSolarAccountsRequest,
  ): ng.IPromise<any> => {
    var url = `${this.prefix}/searchSolarAccounts`;
    return this.igsApiClientService.post(url, searchSolarAccounts);
  };

  public getCustomerDetails = (
    getCustomerDetails: Models.Customer.SolarCustomerDetailsRequest.ISolarCustomerDetailsRequest,
  ): ng.IPromise<any> => {
    var url = `${this.prefix}/GetCustomerDetails`;
    var config = { errorHandler: () => {} };
    return this.igsApiClientService.post(url, getCustomerDetails, config);
  };

  public getOpportunityNumberByIgsCustomerNumber = (igsCustomerNumber: string): ng.IPromise<string> => {
    var url = `${this.prefix}/${igsCustomerNumber}/OpportunityNumber`;
    var config = { errorHandler: () => {} };
    return this.igsApiClientService.get(url, config);
  };

  public getNoteTypes = (): ng.IPromise<any> => {
    var url = `${this.prefix}/GetNoteTypes`;
    var config = { nomask: true };
    return this.igsApiClientService.post(url, config);
  };

  public submitNoteCreation = (
    createNoteRequest: Models.Customer.CustomerNoteModel.ICustomerNoteModel,
  ): ng.IPromise<any> => {
    var url = `${this.prefix}/CreateNote`;
    var config = { nomask: true };
    return this.igsApiClientService.post(url, createNoteRequest, config);
  };

  public getLatestNotes = (getLatestNotes: Models.Customer.SolarNoteRequest.ISolarNoteRequest): ng.IPromise<any> => {
    var url = `${this.prefix}/GetLatestNotes`;
    var config = { nomask: true, errorHandler: () => {} };
    return this.igsApiClientService.post(url, getLatestNotes, config);
  };

  public getAllOtherNotes = (
    getAllOtherNotes: Models.Customer.SolarNoteRequest.ISolarNoteRequest,
  ): ng.IPromise<any> => {
    var url = `${this.prefix}/GetOtherNotes`;
    var config = { nomask: true };
    return this.igsApiClientService.post(url, getAllOtherNotes, config);
  };

  public getCustomerEditables = (igsCustomerNumber: string): ng.IPromise<any> => {
    var url = `${this.prefix}/${igsCustomerNumber}/Editables`;
    var config = { errorHandler: () => {} };
    return this.igsApiClientService.get(url, config);
  };

  public submitEditedCustomer = (
    editedCustomerProfile: Models.Customer.EditCustomerProfile.IEditCustomerProfile,
  ): ng.IPromise<any> => {
    var url = `${this.prefix}/SubmitEditedCustomer`;
    var config = { errorHandler: () => {} };
    return this.igsApiClientService.post(url, editedCustomerProfile, config);
  };

  public editAssignedSalesRepresentatives = (
    igsCustomerNumber: string,
    editedCustomerProfile: Models.Customer.EditCustomerProfile.IEditCustomerProfile,
  ): ng.IPromise<any> => {
    var url = `${this.prefix}/${igsCustomerNumber}/AssignedSalesRepresentatives`;
    var config = { errorHandler: () => { } };
    return this.igsApiClientService.post(url, editedCustomerProfile, config);
  };

  public getCurentSolarAccountBalance = (
    solarAccountBalanceRequest: Models.Customer.SolarAccountBalanceRequest.ISolarAccountBalanceRequest,
  ): ng.IPromise<any> => {
    var url = `${this.prefix}/GetSolarCurrentAccountBalance`;
    return this.igsApiClientService.post(url, solarAccountBalanceRequest);
  };

  public getCustomerTerritory = (
    igsCustomerNumber: string,
    partnerNumber: string,
    leadNumber: string,
  ): ng.IPromise<Models.CostCalculation.Territory.Territory> => {
    var url = `${this.prefix}/Territory?igsCustomerNumber=${igsCustomerNumber || ""}&partnerSystemNumber=${
      partnerNumber || ""
    }&leadNumber=${leadNumber || ""}`;

    return this.igsApiClientService.get(url);
  };

  public getCustomerAccountStage = (igsCustomerNumber: string): ng.IPromise<string> => {
    var url = `${this.prefix}/${igsCustomerNumber}/AccountStage`;
    return this.igsApiClientService.get(url);
  };

  public getAccountPrePtoView = (igsCustomerNumber: string): ng.IPromise<any> => {
    var url = `${this.prefix}/${igsCustomerNumber}/Account/PrePto`;
    return this.igsApiClientService.get(url);
  };

  public getAccountPostPtoView = (igsCustomerNumber: string): ng.IPromise<any> => {
    var url = `${this.prefix}/${igsCustomerNumber}/Account/PostPto`;
    return this.igsApiClientService.get(url);
  };

  public getCustomerIdentifiers = (igsCustomerNumber: string): ng.IPromise<any> => {
    var url = `${this.prefix}/${igsCustomerNumber}/CustomerIdentifiers`;
    return this.igsApiClientService.get(url);
  };

  public associateRegionToCustomer = (
    request: Models.Customer.AssociateRegionToCustomerRequest.IAssociateRegionToCustomerRequest,
  ): ng.IPromise<Models.Customer.AssociateRegionToCustomerResponse.IAssociateRegionToCustomerResponse> => {
    var url = `${this.prefix}/AssociateRegionToCustomer`;
    var config = { errorHandler: () => {} };
    return this.igsApiClientService.post(url, request, config);
  };

  public getAvailableAccountingInformation = (): ng.IPromise<any> => {
    var url = `${this.prefix}/GetAvailableAccountingInformation`;
    return this.igsApiClientService.get(url);
  };

  public getEstimatedUtilityDetails = (
    identifier: string,
  ): ng.IPromise<Models.Customer.EstimatedUtilityDetail.IEstimatedUtilityDetail> => {
    var url = `${this.prefix}/${identifier}/CustomerEstimatedUtilityDetail`;
    return this.igsApiClientService.get(url);
  };

  public submitEstimatedUtilityDetails = (
    identifier: string,
    customerEstimatedUtilityDetail: Models.Customer.EstimatedUtilityDetail.IEstimatedUtilityDetail,
  ): ng.IPromise<null> => {
    var url = `${this.prefix}/${identifier}/CustomerEstimatedUtilityDetail`;
    return this.igsApiClientService.post(url, customerEstimatedUtilityDetail);
  };

  public getContractOrContractAmendmentInProgress = (igsCustomerNumber: string): ng.IPromise<boolean> => {
    var url = `${this.prefix}/${igsCustomerNumber}/ContractOrContractAmendmentInProgress`;
    return this.igsApiClientService.get(url);
  };

  public getContractOrRateAmendmentInProgress = (igsCustomerNumber: string): ng.IPromise<boolean> => {
    const url = `${this.prefix}/${igsCustomerNumber}/ContractOrRateAmendmentInProgress`;
    return this.igsApiClientService.get(url);
  };

  public transferCustomersContract = (
    contractTransferRequest: ContractTransferRequest,
  ): ng.IPromise<ContractTransfer> => {
    var url = `${this.prefix}/${contractTransferRequest.igsCustomerNumberToTransfer}/Contract/Transfer`;
    return this.igsApiClientService.post(url, contractTransferRequest);
  };

  public isEventGroupCompletionEventInProgress = (igsCustomerNumber: string): ng.IPromise<boolean> => {
    var url = `${this.prefix}/${igsCustomerNumber}/IsEventGroupCompletionEventInProgress`;
    return this.igsApiClientService.get(url);
  };

  public cancelCustomer = (
    request: Models.Customer.CancelCustomerRequest.ICancelCustomerRequest,
  ): ng.IPromise<void> => {
    var url = `${this.prefix}/Cancel`;
    return this.igsApiClientService.post(url, request);
  };

  public getCancelReasons = (includeInactive = false): ng.IPromise<Models.Customer.CancelReason.ICancelReason[]> => {
    const url = `Admin/CancelReasons?includeInactive=${includeInactive}`;
    return this.igsApiClientService.get(url);
  };

  public sendMyAccountLink = (igsCustomerNumber: string): ng.IPromise<Models.BaseApiResponse.BaseApiResponse> => {
    const url = `${this.prefix}/SendMyAccountLink`;
    return this.igsApiClientService.post(url, {
      igsCustomerNumber,
    });
  };

  public getActiveIncentivePrograms = (igsCustomerNumber: string): ng.IPromise<IncentiveProgram[]> => {
    const url = `${this.prefix}/${igsCustomerNumber}/ActiveIncentivePrograms`;
    return this.igsApiClientService.get(url);
  };

  public getCustomer = (igsCustomerNumber: string): ng.IPromise<Customer> => {
    const url = `${this.prefix}/${igsCustomerNumber}`;
    return this.igsApiClientService.get(url);
  };

  public submitCustomerEventBypass = (igsCustomerNumber: string, eventCode: string): ng.IPromise<null> => {
    var url = `${this.prefix}/${igsCustomerNumber}/bypassEvent`;
    return this.igsApiClientService.post(url, {
      igsCustomerNumber: igsCustomerNumber,
      eventCode: eventCode,
    });
  };

  public getAccount = (igsCustomerNumber: string): ng.IPromise<Models.Customer.Account.Account> => {
    const url = `${this.prefix}/${igsCustomerNumber}/Account`;
    return this.igsApiClientService.get(url);
  };

  public removeActiveWriteOffStatus = (igsCustomerNumber: string): ng.IPromise<any> => {
    const url = `${this.prefix}/${igsCustomerNumber}/RemoveActiveWriteOffStatus`;
    return this.igsApiClientService.post(url, {
      igsCustomerNumber,
    });
  };

  public validatePrimaryEmailAddress = (igsCustomerNmber: string): ng.IPromise<boolean> => {
    const url = `${this.prefix}/${igsCustomerNmber}/ValidatePrimaryEmailAddress`;
    return this.igsApiClientService.get(url);
  };

  public reactivateCustomer = (identifer: string): IPromise<any> => {
    const url = `${this.prefix}/${identifer}/ReactivateCustomer`;
    return this.igsApiClientService.post(url);
  };

  public getCommunicationTypes = (): ng.IPromise<any> => {
    var url = `/GetCommunicationTypes`;
    return this.igsApiClientService.get(url);
  };
}
